import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import { useTheme } from "../../Themecontext/Themecontext";
import { useDispatch, useSelector } from "react-redux";
import { SaveCustomMenu } from "../../Redux/Slice/UserRight/UserRightSlice";
import { toast } from "react-toastify";
import { Sliders, Search, Trash2 } from "lucide-react";

const CustomMenuModal = ({ show, onHide, menuData, onSaved }) => {
  const { theme } = useTheme();
  const isdark = theme;
  const dispatch = useDispatch();

  const { customMenu: savedCustomMenu, customMenuLoading } = useSelector(
    (state) => state.UserRightSlice
  );

  const [selectedPages, setSelectedPages] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Sync selection from Redux state whenever modal opens
  useEffect(() => {
    if (show) {
      setSelectedPages(savedCustomMenu || []);
      setSearchTerm("");
    }
  }, [savedCustomMenu, show]);

  const handleToggle = (pagePath, pageName) => {
    const isSelected = selectedPages.some((p) => p.path === pagePath);
    if (isSelected) {
      setSelectedPages(selectedPages.filter((p) => p.path !== pagePath));
    } else {
      setSelectedPages([...selectedPages, { path: pagePath, name: pageName }]);
    }
  };

  const handleClearAll = () => {
    setSelectedPages([]);
  };

  const handleSave = async () => {
    const result = await dispatch(SaveCustomMenu(selectedPages));
    if (result.meta.requestStatus === "fulfilled") {
      toast.success("Custom menu saved successfully!");
      if (onSaved) onSaved(selectedPages);
      onHide();
    } else {
      toast.error("Failed to save custom menu.");
    }
  };

  // Helper to recursively flatten submenus to find all leaves (pages)
  const getLeafPages = (items) => {
    let pages = [];
    if (!items) return pages;
    items.forEach((item) => {
      if (item.path) {
        pages.push(item);
      }
      if (item.submenu) {
        pages = [...pages, ...getLeafPages(item.submenu)];
      }
    });
    return pages;
  };

  // Show menus that have submenus or direct paths
  const availableMenus = menuData.filter(
    (menu) =>
      menu.name !== "OVERALL DASHBOARD" &&
      menu.name !== "MY CUSTOM MENU" &&
      ((menu.submenu && menu.submenu.length > 0) || menu.path)
  );

  const modalBg = isdark === "dark" ? "#0b0f19" : "#ffffff";
  const headerBg = isdark === "dark" ? "#111827" : "#f8fafc";
  const textColor = isdark === "dark" ? "#f9fafb" : "#111827";
  const borderColor = isdark === "dark" ? "#1f2937" : "#e5e7eb";
  const categoryColor = isdark === "dark" ? "#3b82f6" : "#2563eb";

  // Check if any modules have matches
  let totalMatchCount = 0;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      dialogClassName="custom-menu-modal"
    >
      <style>{`
        .custom-menu-modal .modal-content {
          background-color: ${modalBg} !important;
          border: 1px solid ${borderColor} !important;
          border-radius: 20px !important;
          overflow: hidden;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.35) !important;
        }
        .custom-menu-modal .modal-header {
          background-color: ${headerBg} !important;
          border-bottom: 1px solid ${borderColor} !important;
          padding: 20px 24px !important;
        }
        .custom-menu-modal .modal-footer {
          background-color: ${headerBg} !important;
          border-top: 1px solid ${borderColor} !important;
          padding: 16px 24px !important;
        }
        .custom-menu-modal .btn-close {
          filter: ${isdark === "dark" ? "invert(1) grayscale(1) brightness(2)" : "none"};
        }
        .modal-grid-columns {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 24px;
          align-items: start;
          width: 100%;
        }
        .custom-menu-card-wrapper {
          width: 100%;
        }
        @media (max-width: 768px) {
          .modal-grid-columns {
            grid-template-columns: 1fr;
          }
        }
        .custom-menu-card {
          background: ${isdark === "light" ? "#f9fafb" : "#111827"} !important;
          border: 1px solid ${borderColor} !important;
          border-radius: 16px;
          padding: 20px;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .custom-menu-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 20px -5px rgba(0,0,0,0.06);
          border-color: ${categoryColor} !important;
        }
        .custom-menu-item-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 14px;
          border-radius: 10px;
          border: 1px solid ${borderColor};
          cursor: pointer;
          transition: all 0.2s ease;
          margin-bottom: 8px;
        }
        .custom-menu-item-row:hover {
          background: ${isdark === 'light' ? 'rgba(37,99,235,0.04)' : 'rgba(59,130,246,0.06)'} !important;
          transform: scale(1.01);
        }
        
        /* Custom Toggle Switch styling */
        .custom-menu-checkbox .form-check-input {
          cursor: pointer;
          width: 38px !important;
          height: 18px !important;
          background-image: none !important;
          background-color: ${isdark === 'light' ? '#cbd5e1' : '#374151'} !important;
          border: none !important;
          border-radius: 20px !important;
          position: relative;
          transition: all 0.3s ease;
        }
        .custom-menu-checkbox .form-check-input:checked {
          background-color: ${categoryColor} !important;
        }
        .custom-menu-checkbox .form-check-input::after {
          content: "";
          position: absolute;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background-color: white !important;
          top: 2px;
          left: 2px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .custom-menu-checkbox .form-check-input:checked::after {
          left: 22px;
        }
      `}</style>

      <Modal.Header
        closeButton
      >
        <Modal.Title style={{ color: textColor, fontWeight: 800, display: "flex", alignItems: "center", gap: "10px", fontSize: "1.3rem" }}>
          <Sliders size={22} style={{ color: categoryColor }} />
          Customize My Menu
        </Modal.Title>
      </Modal.Header>

      <Modal.Body
        className="sidebar-scroller"
        style={{
          background: modalBg,
          color: textColor,
          maxHeight: "65vh",
          overflowY: "auto",
          padding: "24px",
        }}
      >
        {/* Top Control Bar */}
        <Row className="mb-4 align-items-center g-3">
          <Col xs={12} sm={8}>
            <div style={{ position: "relative" }}>
              <Search
                size={18}
                style={{
                  position: "absolute",
                  left: "14px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: isdark === "light" ? "#94a3b8" : "#6b7280",
                }}
              />
              <Form.Control
                type="text"
                placeholder="Search menu pages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  borderRadius: "12px",
                  padding: "11px 16px 11px 40px",
                  border: `1.5px solid ${borderColor}`,
                  background: isdark === "light" ? "#f9fafb" : "#111827",
                  color: textColor,
                  fontWeight: "500",
                  fontSize: "0.95rem",
                  boxShadow: "none",
                  transition: "all 0.2s ease",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = categoryColor;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = borderColor;
                }}
              />
            </div>
          </Col>
          <Col xs={12} sm={4} className="text-sm-end">
            <Button
              onClick={handleClearAll}
              disabled={selectedPages.length === 0}
              variant="outline-danger"
              style={{
                borderRadius: "12px",
                padding: "10px 16px",
                fontWeight: "600",
                fontSize: "0.9rem",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                borderWidth: "1.5px",
                width: "100%",
                justifyContent: "center",
              }}
            >
              <Trash2 size={16} />
              Clear Selections ({selectedPages.length})
            </Button>
          </Col>
        </Row>

        {(() => {
          const activeMenus = availableMenus.map((menu) => {
            const rawPages = menu.submenu ? getLeafPages(menu.submenu) : (menu.path ? [{ name: menu.name, path: menu.path }] : []);
            const leafPages = rawPages.filter((page) =>
              page.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
            return { ...menu, leafPages };
          }).filter((menu) => menu.leafPages.length > 0);

          const totalMatchCount = activeMenus.reduce((acc, menu) => acc + menu.leafPages.length, 0);

          const leftColumnMenus = [];
          const rightColumnMenus = [];
          let leftHeight = 0;
          let rightHeight = 0;

          if (activeMenus.length > 0) {
            const lastMenu = activeMenus[activeMenus.length - 1];
            // Pre-allocate the last menu's height to the left column to balance properly
            leftHeight = lastMenu.leafPages.length;

            const otherMenus = activeMenus.slice(0, -1);
            otherMenus.forEach((menu) => {
              const menuHeight = menu.leafPages.length;
              if (leftHeight <= rightHeight) {
                leftColumnMenus.push(menu);
                leftHeight += menuHeight;
              } else {
                rightColumnMenus.push(menu);
                rightHeight += menuHeight;
              }
            });

            // Append the last menu to the left column so it sits at the bottom-left
            leftColumnMenus.push(lastMenu);
          }

          return (
            <Row className="g-4">
              <Col xs={12} md={6} style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                {leftColumnMenus.map((menu) => {
                  const originalIndex = activeMenus.indexOf(menu);
                  return (
                    <div className="custom-menu-card" key={`left-${originalIndex}`}>
                      <h6
                        style={{
                          color: categoryColor,
                          fontWeight: 800,
                          fontSize: "0.85rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.07em",
                          marginBottom: "16px",
                          borderBottom: `1.5px solid ${borderColor}`,
                          paddingBottom: "10px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        {React.isValidElement(menu.icon) ? React.cloneElement(menu.icon, { size: 16, style: { color: categoryColor } }) : null}
                        <span>{menu.name}</span>
                      </h6>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        {menu.leafPages.map((sub, subIdx) => {
                          const isChecked = selectedPages.some(
                            (p) => p.path === sub.path
                          );
                          return (
                            <div
                              className="custom-menu-item-row"
                              key={subIdx}
                              onClick={() => handleToggle(sub.path, sub.name)}
                              style={{
                                background: isChecked
                                  ? (isdark === 'light' ? 'rgba(37,99,235,0.04)' : 'rgba(59,130,246,0.06)')
                                  : (isdark === 'light' ? '#ffffff' : '#111827'),
                                borderColor: isChecked ? categoryColor : borderColor,
                              }}
                            >
                              <span style={{
                                fontSize: "13px",
                                fontWeight: isChecked ? "600" : "500",
                                color: isChecked
                                  ? (isdark === 'light' ? '#1d4ed8' : '#60a5fa')
                                  : (isdark === 'light' ? '#374151' : '#cbd5e1'),
                              }}>
                                {sub.name}
                              </span>
                              <Form.Check
                                type="switch"
                                id={`custom-menu-check-left-${originalIndex}-${subIdx}`}
                                checked={isChecked}
                                onChange={() => handleToggle(sub.path, sub.name)}
                                onClick={(e) => e.stopPropagation()} // prevent double triggers
                                className="custom-menu-checkbox"
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </Col>

              <Col xs={12} md={6} style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                {rightColumnMenus.map((menu) => {
                  const originalIndex = activeMenus.indexOf(menu);
                  return (
                    <div className="custom-menu-card" key={`right-${originalIndex}`}>
                      <h6
                        style={{
                          color: categoryColor,
                          fontWeight: 800,
                          fontSize: "0.85rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.07em",
                          marginBottom: "16px",
                          borderBottom: `1.5px solid ${borderColor}`,
                          paddingBottom: "10px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        {React.isValidElement(menu.icon) ? React.cloneElement(menu.icon, { size: 16, style: { color: categoryColor } }) : null}
                        <span>{menu.name}</span>
                      </h6>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        {menu.leafPages.map((sub, subIdx) => {
                          const isChecked = selectedPages.some(
                            (p) => p.path === sub.path
                          );
                          return (
                            <div
                              className="custom-menu-item-row"
                              key={subIdx}
                              onClick={() => handleToggle(sub.path, sub.name)}
                              style={{
                                background: isChecked
                                  ? (isdark === 'light' ? 'rgba(37,99,235,0.04)' : 'rgba(59,130,246,0.06)')
                                  : (isdark === 'light' ? '#ffffff' : '#111827'),
                                borderColor: isChecked ? categoryColor : borderColor,
                              }}
                            >
                              <span style={{
                                fontSize: "13px",
                                fontWeight: isChecked ? "600" : "500",
                                color: isChecked
                                  ? (isdark === 'light' ? '#1d4ed8' : '#60a5fa')
                                  : (isdark === 'light' ? '#374151' : '#cbd5e1'),
                              }}>
                                {sub.name}
                              </span>
                              <Form.Check
                                type="switch"
                                id={`custom-menu-check-right-${originalIndex}-${subIdx}`}
                                checked={isChecked}
                                onChange={() => handleToggle(sub.path, sub.name)}
                                onClick={(e) => e.stopPropagation()} // prevent double triggers
                                className="custom-menu-checkbox"
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </Col>

              {totalMatchCount === 0 && (
                <Col xs={12}>
                  <div className="text-center py-5 w-100">
                    <div style={{ fontSize: "2rem", marginBottom: "10px" }}>🔍</div>
                    <h5 style={{ fontWeight: "700", color: isdark === "light" ? "#64748b" : "#94a3b8" }}>No pages found</h5>
                    <p style={{ color: isdark === "light" ? "#94a3b8" : "#4b5563", fontSize: "0.9rem" }}>
                      Try searching for a different name
                    </p>
                  </div>
                </Col>
              )}
            </Row>
          );
        })()}
      </Modal.Body>

      <Modal.Footer>
        <Button
          onClick={onHide}
          disabled={customMenuLoading}
          style={{
            borderRadius: "12px",
            padding: "10px 24px",
            fontWeight: "600",
            background: isdark === "light" ? "#e5e7eb" : "#1f2937",
            color: isdark === "light" ? "#4b5563" : "#d1d5db",
            border: "none",
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = isdark === "light" ? "#d1d5db" : "#374151";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = isdark === "light" ? "#e5e7eb" : "#1f2937";
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={customMenuLoading}
          style={{
            borderRadius: "12px",
            padding: "10px 28px",
            fontWeight: "600",
            background: categoryColor,
            border: "none",
            color: "#ffffff",
            minWidth: 150,
            transition: "all 0.25s ease",
            boxShadow: `0 4px 14px ${isdark === 'light' ? 'rgba(37,99,235,0.25)' : 'rgba(59,130,246,0.3)'}`,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.filter = "brightness(1.1)";
            e.currentTarget.style.transform = "translateY(-1.5px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.filter = "none";
            e.currentTarget.style.transform = "none";
          }}
        >
          {customMenuLoading ? (
            <div className="d-flex align-items-center justify-content-center">
              <Spinner animation="border" size="sm" className="me-2" />
              Saving...
            </div>
          ) : (
            "Save Changes"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CustomMenuModal;
